import React from 'react';
import { Button } from '../../ui/button';
import { ExternalLink, Edit, Trash2, Users, Clock, Folder } from 'lucide-react';
import { categoryIcons, statusColors } from './constants';

// Grid view card
export const AppCard = ({ app, onOpen, onEdit, onDelete }) => {
  const CategoryIcon = categoryIcons[app.category] || Folder;
  const StatusInfo = statusColors[app.status] || statusColors.active;
  const StatusIcon = StatusInfo.icon;

  return (
    <div className="bg-[#302f2c] border border-[#1e3a5f]/50 rounded-xl overflow-hidden hover:border-[#7dd3fc]/30 transition-all group">
      <div className="h-32 bg-[#1a1c1b] flex items-center justify-center relative overflow-hidden">
        {app.icon_url ? (
          <img src={app.icon_url} alt={app.name} className="w-full h-full object-cover" />
        ) : (
          <CategoryIcon className="w-12 h-12 text-[#1e3a5f]" />
        )}
        <div className={`absolute top-3 right-3 ${StatusInfo.bg} ${StatusInfo.text} px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1`}>
          <StatusIcon size={12} />
          {app.status}
        </div>
      </div>

      <div className="p-5">
        <div className="flex items-start justify-between mb-2">
          <div>
            <h3 className="text-[#7dd3fc] font-bold text-lg">{app.name}</h3>
            <div className="flex items-center gap-2 text-[#888680] text-xs">
              <CategoryIcon size={12} />
              <span className="capitalize">{app.category}</span>
            </div>
          </div>
        </div>

        <p className="text-[#888680] text-sm line-clamp-2 mb-4">
          {app.description || 'No description'}
        </p>

        <div className="flex items-center gap-4 text-xs text-[#888680] mb-4">
          {app.team_members?.length > 0 && (
            <div className="flex items-center gap-1">
              <Users size={12} />
              {app.team_members.length} members
            </div>
          )}
          {app.last_accessed && (
            <div className="flex items-center gap-1">
              <Clock size={12} />
              {new Date(app.last_accessed).toLocaleDateString()}
            </div>
          )}
        </div>

        <div className="flex gap-2">
          <Button
            onClick={() => onOpen(app)}
            data-testid={`open-app-${app.app_id}`}
            className="flex-1 bg-[#7dd3fc] text-[#1a1c1b] hover:bg-[#7dd3fc]/90 rounded-full py-2 text-sm font-medium flex items-center justify-center gap-1"
          >
            <ExternalLink size={14} />
            Open Admin
          </Button>
          <Button
            onClick={() => onEdit(app)}
            variant="outline"
            data-testid={`edit-app-${app.app_id}`}
            className="border-[#1e3a5f] text-[#888680] hover:text-[#7dd3fc] rounded-full py-2 px-3"
          >
            <Edit size={14} />
          </Button>
          <Button
            onClick={() => onDelete(app.app_id)}
            variant="outline"
            data-testid={`delete-app-${app.app_id}`}
            className="border-[#1e3a5f] text-[#888680] hover:text-red-500 rounded-full py-2 px-3"
          >
            <Trash2 size={14} />
          </Button>
        </div>
      </div>
    </div>
  );
};

// List view row
export const AppListItem = ({ app, onOpen, onEdit, onDelete }) => {
  const CategoryIcon = categoryIcons[app.category] || Folder;
  const StatusInfo = statusColors[app.status] || statusColors.active;
  const StatusIcon = StatusInfo.icon;

  return (
    <div className="bg-[#302f2c] border border-[#1e3a5f]/50 rounded-xl p-4 hover:border-[#7dd3fc]/30 transition-all flex items-center gap-4">
      <div className="w-14 h-14 bg-[#1a1c1b] rounded-lg flex items-center justify-center overflow-hidden flex-shrink-0">
        {app.icon_url ? (
          <img src={app.icon_url} alt={app.name} className="w-full h-full object-cover" />
        ) : (
          <CategoryIcon className="w-6 h-6 text-[#1e3a5f]" />
        )}
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <h3 className="text-[#7dd3fc] font-bold truncate">{app.name}</h3>
          <div className={`${StatusInfo.bg} ${StatusInfo.text} px-2 py-0.5 rounded-full text-xs font-medium flex items-center gap-1`}>
            <StatusIcon size={10} />
            {app.status}
          </div>
        </div>
        <p className="text-[#888680] text-sm truncate">{app.description || 'No description'}</p>
      </div>

      <div className="hidden md:flex items-center gap-6 text-sm text-[#888680]">
        <div className="flex items-center gap-1">
          <CategoryIcon size={14} />
          <span className="capitalize">{app.category}</span>
        </div>
        {app.team_members?.length > 0 && (
          <div className="flex items-center gap-1">
            <Users size={14} />
            {app.team_members.length}
          </div>
        )}
      </div>

      <div className="flex items-center gap-2">
        <Button
          onClick={() => onOpen(app)}
          data-testid={`open-app-${app.app_id}`}
          className="bg-[#7dd3fc] text-[#1a1c1b] hover:bg-[#7dd3fc]/90 rounded-full px-4 py-2 text-sm font-medium flex items-center gap-1"
        >
          <ExternalLink size={14} />
          Open
        </Button>
        <Button
          onClick={() => onEdit(app)}
          variant="outline"
          data-testid={`edit-app-${app.app_id}`}
          className="border-[#1e3a5f] text-[#888680] hover:text-[#7dd3fc] rounded-full p-2"
        >
          <Edit size={14} />
        </Button>
        <Button
          onClick={() => onDelete(app.app_id)}
          variant="outline"
          data-testid={`delete-app-${app.app_id}`}
          className="border-[#1e3a5f] text-[#888680] hover:text-red-500 rounded-full p-2"
        >
          <Trash2 size={14} />
        </Button>
      </div>
    </div>
  );
};
